import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { TicketPrefixField } from '../components/TicketPrefixField.js';
import { cleanTicketPrefix, ticketPrefixProblem } from '../lib/ticket-prefix.js';
import { suggestsListing } from '../lib/support-listing.js';
import {
  adminApi,
  type Application,
  type HealthResult,
  type SecurityGroup,
} from '../lib/api.js';
import { useAsync, useAction, type Async } from '../lib/useAsync.js';
import { count, duration } from '../lib/format.js';
import { formatPlatforms, parsePlatforms, platformLabel } from '../lib/platforms.js';
import { defaultRequirement, describeRequirement, type Requirement } from '../lib/tester-requirements.js';
import { Chip, EditPlatforms } from '../modals/EditPlatforms.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { PickList } from '../components/PickList.js';
import { ApplicationTiers } from './ApplicationTiers.js';
import { ApplicationReleases } from './ApplicationReleases.js';
import { RecordScreen, resolveTab, type RecordTabs } from '../components/Record.js';
import { InfoBubble, inputStyle } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  DotPill,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  Switch,
} from '../components/ui.js';
import { needsClosureHook, saveOutcome } from '../lib/closure-config.js';
import { AppRoles } from '../modals/AppRoles.js';
import { ApplicationBranding } from './ApplicationBranding.js';

export const STATUSES = ['planning', 'in-dev', 'alpha', 'beta', 'live', 'retired'] as const;

/**
 * Does what the admin typed confirm retiring THIS application?
 *
 * Trimmed and case-insensitive on purpose. The name is being retyped from a
 * label a few pixels away, not recalled from memory, so a stray trailing space
 * or a lowercased first letter is the same demonstrated intent. Refusing those
 * does not make anyone read more carefully; it teaches people to paste the
 * name, which removes the pause the box exists to create.
 *
 * A BLANK TARGET MUST NOT MATCH. `''.trim() === ''.trim()` is true, so the
 * obvious comparison turns the guard off entirely for an application whose name
 * is empty or whitespace — the untouched box would confirm on its own. That is
 * the one case where a wrong reading is silent and destructive at once, and an
 * unnamed application is more likely to be a mistake, not less.
 */
export function confirmsRetire(typed: string, appName: string): boolean {
  const target = appName.trim().toLowerCase();
  if (!target) return false;
  return typed.trim().toLowerCase() === target;
}

/**
 * One application: who can reach it, what it is, and how to retire it.
 *
 * ── Retiring is in the header, not in a screen-level action row ──────
 *
 * A retired app grants nobody access, which means agreements scoped to it stop
 * being owed by anybody. That is correct — a dead app's consent requirements
 * should not follow people around — and it is a far larger effect than "hide it
 * from a list", so it belongs next to the specific application it affects,
 * behind a confirmation, with the count of what it touched reported back.
 *
 * The old portal put every consequential action in an inline disclosure below
 * the table. The instinct was right and the location was not: what makes this
 * safe is that it is attached to one record and asks before acting, not that it
 * is hidden. A CONFIRMATION is still a modal — that has not changed and should
 * not. The record around it is a screen.
 */
const TABS = [
  { path: 'settings', label: 'Settings' },
  /*
    "Security" rather than "Reachable by", because it holds two different
    questions about the same application: WHO may open it, and what ROLES it
    recognises once they are inside. The design canvas names it that, and the
    pairing is the reason — the two are read together when somebody asks what a
    person can do in this app.
  */
  { path: 'security', label: 'Security' },
  /* The paid tiers it sells, and the RevenueCat entitlement behind each
     (RidgeWill/auth#567). Next to Security: both decide what somebody gets. */
  { path: 'tiers', label: 'Tiers' },
  { path: 'health', label: 'Health' },
  /* What each released version changed, shown in the app as "What's new"
     (RidgeWill/keyodo#709). Beside Branding: both are written here and read by
     people outside the portal. */
  { path: 'releases', label: 'Releases' },
  /*
    Last, because it is the only tab that changes what somebody OUTSIDE the
    portal sees. Everything above answers a question about the application;
    this one edits the page a signed-out person is looking at.
    RidgeWill/org-admin#86.
  */
  { path: 'branding', label: 'Branding' },
] as const satisfies RecordTabs;

export function ApplicationDetail({ id, tab }: { id: string; tab?: string }) {
  const { state, reload } = useAsync(() => adminApi.application(id), [id]);
  const current = resolveTab(TABS, tab);

  return (
    <AsyncBoundary state={state}>
      {(app) => (
        <RecordScreen
          back={{ to: '/applications', label: 'Applications' }}
          title={app.name}
          identifier={app.slug}
          badges={app.active ? null : <Badge tone="danger">retired</Badge>}
          description={app.description || undefined}
          actions={<RetireControl app={app} onChanged={reload} />}
          tabs={TABS}
          /**
           * The SLUG, not the id (RidgeWill/org-admin#86).
           *
           * It is the better key of the two and the schema says why: the slug
           * is IMMUTABLE because it appears in a token's `aud` and in
           * `Agreement.appSlug`, neither of which is a foreign key. So a link
           * pasted into an incident channel keeps meaning the same application,
           * which a surrogate id only accidentally does -- and `#/applications/
           * keyodo/branding` says what it opens without anybody looking it up.
           *
           * `adminApi.application()` resolves either, so id links already in
           * circulation keep working.
           */
          base={`/applications/${encodeURIComponent(app.slug)}`}
          current={current}
        >
          {current === 'settings' ? (
            <>
              <Settings app={app} onChanged={reload} />
              {/* Its own panel, not a field inside Settings: it saves through a
                  different endpoint, and one panel with two Save buttons is how
                  somebody edits a URL, presses the wrong one and believes it
                  saved. `key` on the webhook so the panel re-reads
                  `deliverable` after Settings changes it. */}
              <EventSubscriptions key={app.eventWebhookUrl ?? ''} app={app} />
            </>
          ) : null}
          {current === 'security' ? (
            <>
              <Grants app={app} onChanged={reload} />
              <AppRoles applicationId={app.id} appSlug={app.slug} />
            </>
          ) : null}
          {current === 'tiers' ? <ApplicationTiers applicationId={app.id} appSlug={app.slug} /> : null}
          {current === 'health' ? <Health app={app} /> : null}
          {current === 'releases' ? <ApplicationReleases applicationId={app.id} appName={app.name} /> : null}
          {current === 'branding' ? <ApplicationBranding app={app} /> : null}
        </RecordScreen>
      )}
    </AsyncBoundary>
  );
}

/**
 * Which SECURITY GROUPS entitle this application.
 *
 * These were platform roles until RidgeWill/auth#146. Entitlement was a property
 * of a role, so granting an application to everybody meant minting a role to
 * carry it — `keyodo-user`, whose permissions were identical to `user`. It is
 * assigned to groups now, and `Basic Users` is the group every account joins on
 * creation, so granting it there is what "everyone gets this" means.
 *
 * THE SAME ROWS ARE EDITABLE FROM THE GROUP, on the Security groups screen, and
 * both write the COMPLETE set. So the last save wins, visibly, rather than two
 * admins each half-succeeding — the trade the auth service makes deliberately.
 * Worth knowing here: a grant that appears to have reverted was probably
 * replaced from the other end rather than failing.
 */
function Grants({ app, onChanged }: { app: Application; onChanged: () => void }) {
  const known = useAsync(() => adminApi.securityGroups());
  const [adding, setAdding] = useState(false);
  const action = useAction();
  const mayWrite = useCan('application:write');

  /**
   * A TABLE WITH ITS OWN ADD AND REMOVE, not a checklist and a Save (Jon,
   * 2026-08-28).
   *
   * ── What was wrong with the checklist ────────────────────────────
   *
   * It was the only place in the portal where changing who can reach something
   * meant ticking boxes and then finding a Save. Every other entitlement surface
   * — the Access tab's four panels, the group screen's membership — is a table
   * of what is granted, with `+ Add` above it and a Remove on each row. So this
   * one screen asked people to learn a second interaction for the same idea,
   * and it was the screen where forgetting to press Save silently changes
   * nothing.
   *
   * It also displayed badly as it grew: a row of checkboxes says which groups
   * exist, in an order nobody chose, with the two or three that actually matter
   * scattered through them. A table says which groups are granted.
   *
   * ── The write is still the COMPLETE set ───────────────────────────
   *
   * `setApplicationGrants` replaces everything, which has not changed and must
   * not — the same rows are editable from the group screen, and a whole-set
   * write is what makes two admins editing at once resolve to one of their
   * intentions rather than a merge neither asked for. Add sends the current set
   * plus what was picked; Remove sends it minus one. The difference from the old
   * control is that each of those is one deliberate action rather than an
   * accumulated diff somebody has to remember to commit.
   *
   * THE OTHER HALF OF THE PAIR IS NOT ON THIS SCREEN. Membership is edited from
   * the person, not from the group — which is what makes withdrawing a universal
   * entitlement from one account possible without touching anybody else.
   */
  const save = (next: string[], describeIt: (groups: string[]) => string) =>
    void action
      .run(async () => describeIt((await adminApi.setApplicationGrants(app.id, next)).groups))
      .then(onChanged);

  return (
    <Panel
      title="App entitlements"
      info={
        <InfoBubble label="Who can reach this application">
          The security groups entitled to open it. Everybody in one of these groups can; nobody else
          can. <strong>Basic Users</strong> is the group every account joins on creation, so granting
          it there is what &ldquo;everyone gets this&rdquo; means.
          <br />
          <br />
          These were platform roles until RidgeWill/auth#146, when granting an application to
          everybody meant minting a role to carry it. The same rows are editable from the group&rsquo;s
          own screen, and both ends write the complete set &mdash; so a grant that looks like it
          reverted was probably replaced from the other end rather than failing.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !mayWrite}
          title={mayWrite ? undefined : whyDisabled('changing entitlement')}
          onClick={() => setAdding(true)}
        >
          + Add group
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}

      {app.groups.length === 0 ? (
        /* Registered and reachable by nobody. A legitimate state, and one that
           looks identical to a working application if it renders as a blank. */
        <EmptyState
          title="Reachable by nobody"
          suggestion="The application is registered and no group is entitled to it, so no account can open it. Add a group, or leave it this way deliberately while it is being built."
        />
      ) : (
        <Table columns={['Security group', 'Members', '']} actionsLast>
          {app.groups.map((slug) => (
            <tr key={slug} className="rw-rowhover">
              <Td>
                <GroupLabel slug={slug} known={known.state} />
              </Td>
              <Td>
                <GroupMembers slug={slug} known={known.state} />
              </Td>
              <Td action>
                {/*
                  A HOLD, like every other Remove that withdraws access.

                  This one is the widest of them: removing `basic-users` from a
                  live application takes the application away from every account
                  on the platform at once, from a row that says two words. The
                  fill is the only thing on the control that says so.
                */}
                <HoldButton
                  disabled={action.running}
                  title={`Hold for a second and a half to stop ${slug} reaching this application. Everybody in that group loses access immediately.`}
                  onHold={() =>
                    save(
                      app.groups.filter((g) => g !== slug),
                      (groups) =>
                        groups.length
                          ? `${slug} can no longer reach it. Still reachable by ${groups.join(', ')}.`
                          : `${slug} can no longer reach it. Nobody can reach it now.`,
                    )
                  }
                >
                  Remove
                </HoldButton>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={known.state}>
          {(d) => (
            <PickList
              title="Entitle a security group"
              hint="Everybody in the group can open this application, immediately. Basic Users is every account on the platform."
              options={d.groups
                .filter((g) => !app.groups.includes(g.slug))
                .map((g) => ({ value: g.slug, label: `${g.name} (${count(g.members)})` }))}
              emptyMessage="Every security group is already entitled to this application."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                save([...app.groups, ...picked], (groups) => `Reachable by ${groups.join(', ')}.`);
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * The group's NAME, with its slug beneath it in monospace — the chip rule.
 *
 * The application row stores slugs and only slugs, so the name has to come from
 * the groups listing, which may not have arrived or may have failed. The slug is
 * shown either way: it is what the record actually holds, and a row that renders
 * nothing while a second request is in flight looks like a missing grant.
 *
 * A group named in the grant that the listing does not contain is shown as
 * unknown rather than hidden. A grant that vanishes from the screen while still
 * being in the database is the one thing this table must not do.
 */
function GroupLabel({ slug, known }: { slug: string; known: Async<{ groups: SecurityGroup[] }> }) {
  const match = known.status === 'ready' ? known.data.groups.find((g) => g.slug === slug) : undefined;
  return (
    <>
      <div style={{ fontWeight: 600 }}>
        {match ? match.name : slug}
        {known.status === 'ready' && !match ? <Badge tone="danger">unknown</Badge> : null}
      </div>
      <code style={{ fontSize: '.78rem', color: C.muted }}>{slug}</code>
    </>
  );
}

/** How many people this grant actually reaches. The number is why a row saying
 *  `basic-users` and one saying `admins` are not the same size of decision. */
function GroupMembers({ slug, known }: { slug: string; known: Async<{ groups: SecurityGroup[] }> }) {
  if (known.status !== 'ready') return <span style={{ color: C.muted }}>&mdash;</span>;
  const match = known.data.groups.find((g) => g.slug === slug);
  if (!match) return <span style={{ color: C.muted }}>&mdash;</span>;
  return (
    <span>
      {count(match.members)} {match.members === 1 ? 'account' : 'accounts'}
    </span>
  );
}

function Settings({ app, onChanged }: { app: Application; onChanged: () => void }) {
  const [name, setName] = useState(app.name);
  const [description, setDescription] = useState(app.description);
  /* Parsed once, into the set the checkboxes edit. The column is written back
     through `formatPlatforms`, so a Save that did not touch this field writes
     the same string it read — see the round-trip test. */
  const [platforms, setPlatforms] = useState(() => parsePlatforms(app.platform));
  const [requirement, setRequirement] = useState<Requirement | null>(app.testerRequirement ?? null);
  const [editingPlatforms, setEditingPlatforms] = useState(false);
  const [status, setStatus] = useState(app.status);
  const [healthCheckUrl, setHealth] = useState(app.healthCheckUrl ?? '');
  const [eventWebhookUrl, setWebhook] = useState(app.eventWebhookUrl ?? '');
  const [ticketPrefix, setTicketPrefix] = useState(app.ticketPrefix ?? '');
  const prefix = cleanTicketPrefix(ticketPrefix);
  const [supportListed, setSupportListed] = useState(app.supportListed === true);
  const [testerListed, setTesterListed] = useState(app.testerListed === true);
  // `!== false`, not the raw value: against an auth that predates the field this
  // arrives undefined, and `checked={undefined}` makes React treat the box as
  // uncontrolled. Defaulting to ticked also matches the server and the badge.
  const [holdsUserData, setHoldsUserData] = useState(app.holdsUserData !== false);
  const action = useAction();

  return (
    <Panel title="Settings">
      <PanelBody>
      <Field label="Slug" hint="Permanent. Sending a new one is rejected rather than ignored.">
        <input value={app.slug} readOnly disabled style={{ ...inputStyle, background: C.page }} />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Description">
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
      </Field>
      <Field
        label="Platforms"
        /* It was a text box, which produced `Web`, `web`, `Browser` and
           `iOS · Android` in one column — three different facts to read and
           nothing to sort or filter by. Jon, 2026-08-28: it used to be
           selectable. Anything already typed in is kept; see lib/platforms.ts. */
        hint="Where this application runs, and which devices a person needs to sign up as a tester."
      >
        {/* Chips and the tester rule in a sentence, with the editing in a
            modal (Jon, 2026-10-20: keep it here if it fits). */}
        <span style={{ display: 'grid', gap: '.45rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap' }}>
            {platforms.length ? platforms.map((p) => <Chip key={p}>{platformLabel(p)}</Chip>) : <span style={{ color: C.muted }}>None yet</span>}
            <button type="button" onClick={(e) => { e.preventDefault(); setEditingPlatforms(true); }} style={editLink}>
              Edit platforms
            </button>
          </span>
          <span style={{ fontSize: '.8rem', color: C.textMid }}>
            {describeRequirement(requirement ?? defaultRequirement(platforms))}
            {requirement ? null : <span style={{ color: C.muted }}> Follows the platforms.</span>}
          </span>
        </span>
      </Field>
      {editingPlatforms ? (
        <EditPlatforms
          appName={app.name}
          platforms={platforms}
          requirement={requirement}
          onClose={() => setEditingPlatforms(false)}
          onSave={(nextPlatforms, nextRule) => {
            setPlatforms(nextPlatforms);
            // Kept only when it differs from what the platforms would give.
            const derived = describeRequirement(defaultRequirement(nextPlatforms));
            setRequirement(describeRequirement(nextRule) === derived ? null : nextRule);
            setEditingPlatforms(false);
          }}
        />
      ) : null}
      {/* Beside Status, Jon asked (2026-10-23): the two answer "who sees this
          app" together. Status is only as wide as its longest stage. The switch
          is shown only against an auth that knows the setting, for the same
          reason as the support switch below. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem 2.5rem', alignItems: 'flex-start' }}>
        <div style={{ width: '12rem' }}>
          <Field label="Status">
            <Select
              block
              label="Status"
              value={status}
              onChange={setStatus}
              options={STATUSES.map((v) => ({ value: v, label: v }))}
            />
          </Field>
        </div>
        {app.testerListed !== undefined ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
            <span id="tester-listed-label" style={{ fontWeight: 600, color: C.textMid }}>
              Show on Tester Card form
            </span>
            <span style={{ display: 'flex', alignItems: 'center', minHeight: '2.1rem' }}>
              <Switch labelledBy="tester-listed-label" checked={testerListed} onChange={setTesterListed} />
            </span>
          </div>
        ) : null}
      </div>
      <TicketPrefixField value={ticketPrefix} onChange={setTicketPrefix} saved={app.ticketPrefix ?? null} />
      {/* Only against an auth that knows the setting (auth#573). An older one
          omits it, and its strict update body would refuse the field with a
          400, so a switch shown there could never be saved. */}
      {/* A switch like the tester one above, with its explanation in a
          bubble rather than standing text (Jon, 2026-10-23). */}
      {app.supportListed !== undefined ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '.45rem' }}>
            <span id="support-listed-label" style={{ fontWeight: 600, color: C.textMid }}>
              Show on Support form
            </span>
            <InfoBubble label="About the Support form" align="left">
              Whether customers can pick this app when filing a ticket in the account portal or on the
              website. Tickets sent from inside the app are accepted either way.
              {suggestsListing(status, supportListed) ? (
                <>
                  <br />
                  <br />
                  This app is in {status}, but customers can’t pick it when filing a ticket.
                </>
              ) : null}
            </InfoBubble>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', minHeight: '2.1rem' }}>
            <Switch labelledBy="support-listed-label" checked={supportListed} onChange={setSupportListed} />
          </span>
        </div>
      ) : null}
      <Field
        label="Health check URL"
        /* This hint used to say the service never fetches it. It does now, from
           the admin deployment only, and only when somebody asks — behind a
           guard that refuses private address space, pins the vetted address and
           does not follow redirects. Saying so here matters because the previous
           wording is why an admin might expect a URL typed here to be inert. */
        hint="Checked only when you run the health check, from the admin deployment. URLs resolving to private addresses are refused."
      >
        <input value={healthCheckUrl} onChange={(e) => setHealth(e.target.value)} style={inputStyle} />
      </Field>
      <Field
        label="Holds user data"
        /* Asked BEFORE the webhook, because it decides whether the webhook is a
           question at all.

           Registering an app says which roles may reach it. That is not the same
           as saying it stores anything, and the two only look identical because
           they coincide for product apps. They part company for our own portals:
           this admin portal and the account portal are reachable by everybody and
           hold nothing — they read and write auth's database through auth's API.
           Before this checkbox existed both were in the closure fan-out, so every
           single closure raised two "no endpoint registered" alarms about apps
           that will never have one. */
        hint="Tick if this app keeps its own copy of a user: a profile, their content, anything. Untick only for a front end that reads and writes auth's database rather than one of its own."
      >
        <label style={{ display: 'flex', gap: '.45rem', alignItems: 'center', fontSize: '.85rem' }}>
          <input
            type="checkbox"
            checked={holdsUserData}
            onChange={(e) => setHoldsUserData(e.target.checked)}
          />
          This app stores data about its users
        </label>
      </Field>

      {/* Only asked of an app that holds something.

          An app with nothing to erase has nowhere to be told about, so this is
          not a question it can answer — and an empty box sitting under a hint
          about erasing personal data invites somebody to fill it in to look
          tidy. Registering a URL on a non-holder would be inert, but it would
          also be a claim in the admin portal that is not true anywhere else.

          The typed value is KEPT while hidden rather than cleared, so unticking
          and re-ticking does not cost an admin a URL they already had. The
          fan-out narrows on `holdsUserData` and never on this field, so a
          stored URL on an unticked app changes nothing. */}
      {holdsUserData ? (
        <Field
          label="Event webhook"
          /* Two things in the hint, and both are things an admin gets wrong.

             It must be the PUBLIC hostname: every app on this stack sits behind
             Cloudflare with an origin lock requiring a header Cloudflare injects,
             so a URL pointing at the Railway origin answers 403 — which reads
             like a rejected credential and sends whoever debugs it looking at the
             signing key rather than at this field.

             And empty is not neutral, unlike the health URL above it. The box is
             ticked, so this app holds a copy of somebody: blank means it is never
             told about a closure and quietly keeps that copy, with nothing
             failing to say so. The old wording ended "leave blank only if the app
             holds no personal data", which contradicted itself the moment the
             field stopped rendering for those apps. */
          /* RENAMED from "Identity closure webhook" (RidgeWill/auth#416).

             One URL now carries EVERY platform event, not only closure, and the
             old label was actively misleading: an admin reading it would expect
             a second field for the rest and, not finding one, conclude the bus
             was unconfigured. The field itself is unchanged — this is the same
             column it always was, doing more. */
          hint="Where auth sends this app's platform events, including the notice that a user closed their Ridgewill identity. One URL carries every type. Use the app's public hostname, not its origin."
        >
          <input
            value={eventWebhookUrl}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder="https://api.example.com/internal/events"
            style={inputStyle}
          />
        </Field>
      ) : null}

      {/* Only when the app actually holds something. An unticked app with no
          webhook is correct, and warning about it is how the warning stops
          being read on the apps where it means something. */}
      {needsClosureHook({ active: app.active, holdsUserData, eventWebhookUrl }) ? (
        <p style={{ margin: 0, fontSize: '.79rem', color: C.dangerText }}>
          No webhook registered, so this app is never told when someone closes their account. Its
          copy of their data stays until somebody removes it by hand.
        </p>
      ) : null}

      {/* Unticking is destructive to queued work, so it says so before the save
          rather than in the outcome line afterwards. */}
      {app.holdsUserData !== false && !holdsUserData ? (
        <p style={{ margin: 0, fontSize: '.79rem', color: C.muted }}>
          Saving this discards any closures still queued for this app. Deliveries already made are
          kept.
        </p>
      ) : null}

      <OutcomeNote outcome={action.outcome} />

      <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
        <Button
          disabled={action.running || ticketPrefixProblem(prefix) !== null}
          onClick={() =>
            void action
              .run(async () => {
                const res = await adminApi.updateApplication(app.id, {
                  name,
                  description,
                  platform: formatPlatforms(platforms),
                  // Only when changed, so an auth that predates it is never
                  // sent a field its strict body refuses.
                  ...(JSON.stringify(requirement) !== JSON.stringify(app.testerRequirement ?? null)
                    ? { testerRequirement: requirement }
                    : {}),
                  status,
                  // Empty string is not a URL and the server validates it as
                  // one. Null is how either field is cleared.
                  healthCheckUrl: healthCheckUrl.trim() || null,
                  eventWebhookUrl: eventWebhookUrl.trim() || null,
                  // Only when changed, so saving other settings against an auth
                  // that predates the field does not send one it would refuse.
                  ...(prefix !== (app.ticketPrefix ?? '') ? { ticketPrefix: prefix || null } : {}),
                  // Same reason: only when changed, so an auth that predates it
                  // is never sent a field its strict body refuses.
                  ...(supportListed !== (app.supportListed === true) ? { supportListed } : {}),
                  ...(testerListed !== (app.testerListed === true) ? { testerListed } : {}),
                  holdsUserData,
                });
                // The counts are the part an admin cannot see from the form, so
                // they are the part worth saying. `saveOutcome` is silent when
                // nothing moved.
                return saveOutcome(res);
              })
              .then(onChanged)
          }
        >
          {action.running ? 'Saving…' : 'Save'}
        </Button>
      </div>
      </PanelBody>
    </Panel>
  );
}

/**
 * What to say after saving subscriptions.
 *
 * Exported and tested because this is the sentence where being wrong is
 * SILENT. Subscribing with no webhook registered is allowed — the two settings
 * are made in either order — so the success path can legitimately mean "nothing
 * will be delivered", and an admin who reads "Subscribed to 3 event types" then
 * walks away believing the app is wired up. The only thing that says otherwise
 * is this line.
 *
 * The empty set is its own sentence rather than "0 event types": unsubscribing
 * from everything is a deliberate act, and it should read like one.
 */
export function subscriptionOutcome(res: { eventTypes: string[]; deliverable: boolean }): string {
  const n = res.eventTypes.length;
  if (n === 0) {
    return 'Unsubscribed from every event type. This app now receives only identity closures.';
  }
  const types = `${count(n)} event type${n === 1 ? '' : 's'}`;
  return res.deliverable
    ? `Subscribed to ${types}.`
    : `Saved ${types}, but no webhook is registered to deliver them.`;
}

/**
 * Which platform events this application receives (RidgeWill/auth#416).
 *
 * ── WHY THIS IS NOT A FIELD IN THE SETTINGS FORM ────────────────────
 *
 * It saves through a different endpoint, and one panel with two Save buttons is
 * how somebody edits a URL, presses the wrong button and believes it saved. The
 * two belong on the same TAB, because they are halves of one question — where
 * events go, and which ones — and reading either alone tells you nothing about
 * whether this app hears anything.
 *
 * ── THE MENU COMES FROM THE SERVICE ─────────────────────────────────
 *
 * `available` is fetched, never a list in this file. A type added to auth
 * otherwise ships and nobody can subscribe to it, because the portal does not
 * know it exists — and that failure is silent on both sides.
 *
 * ── `identity.closed` IS ABSENT ON PURPOSE ──────────────────────────
 *
 * It is not in `available` and cannot be sent. It goes to every app holding that
 * user's data, narrowed from their grants, and an app that could unsubscribe
 * from an erasure notice is an app that can quietly stop honouring erasures.
 * Said in the panel rather than left as a gap, because an admin who does not see
 * it in the list will otherwise go looking for where they turned it off.
 */
function EventSubscriptions({ app }: { app: Application }) {
  const { state, reload } = useAsync(() => adminApi.applicationEventSubscriptions(app.id), [app.id]);
  const action = useAction();
  const mayWrite = useCan('application:write');

  return (
    <Panel title="Platform events">
      {/* PanelBody OUTSIDE the boundary, matching `Health` below: it is the
          panel's padding, and inside the boundary the loading and error states
          would render flush against the panel edge. */}
      <PanelBody>
        <AsyncBoundary state={state}>
          {(subs) => (
            <SubscriptionForm
              app={app}
              subs={subs}
              action={action}
              onSaved={reload}
              mayWrite={mayWrite}
            />
          )}
        </AsyncBoundary>
      </PanelBody>
    </Panel>
  );
}

/**
 * Split out so the checkbox state is seeded from loaded data by MOUNTING rather
 * than by an effect that syncs it. A `useState` initialised from a prop inside
 * the boundary above would keep the first response forever, so a save followed
 * by a reload would redraw the old ticks — the shape of bug where the screen
 * disagrees with the database and only a refresh settles it.
 */
function SubscriptionForm({
  app,
  subs,
  action,
  onSaved,
  mayWrite,
}: {
  app: Application;
  subs: { eventTypes: string[]; available: string[]; deliverable: boolean };
  action: ReturnType<typeof useAction>;
  onSaved: () => void;
  mayWrite: boolean;
}) {
  const [picked, setPicked] = useState<string[]>(subs.eventTypes);
  const toggle = (type: string) =>
    setPicked((p) => (p.includes(type) ? p.filter((t) => t !== type) : [...p, type]));

  return (
    /* A grid rather than a fragment: PanelBody's gap applies to ITS direct
       children, and these are one level deeper. */
    <div style={{ display: 'grid', gap: '.8rem' }}>
      {/* The state that looks like a working one. Subscribing with nowhere to
          send is allowed — the two settings can be made in either order — so
          this is the only thing that says the ticks below currently reach
          nobody. */}
      {!subs.deliverable ? (
        <p style={{ margin: 0, fontSize: '.79rem', color: C.dangerText }}>
          No event webhook registered, so nothing ticked here is delivered anywhere. Set one under
          Settings above.
        </p>
      ) : null}

      <Field
        label="Subscribed events"
        hint="Opt in per type. An event this app has not asked for never arrives, so a new type cannot land in its inbox as an unhandled surprise."
      >
        {subs.available.length === 0 ? (
          <Note>This service publishes no subscribable event types.</Note>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            {subs.available.map((type) => (
              <label
                key={type}
                style={{ display: 'flex', gap: '.45rem', alignItems: 'center', fontSize: '.85rem' }}
              >
                <input
                  type="checkbox"
                  checked={picked.includes(type)}
                  disabled={!mayWrite}
                  onChange={() => toggle(type)}
                />
                <code>{type}</code>
              </label>
            ))}
          </div>
        )}
      </Field>

      <p style={{ margin: 0, fontSize: '.79rem', color: C.muted }}>
        <code>identity.closed</code> is not listed because it cannot be turned off. It goes to every
        application holding that user&rsquo;s data, whether or not it appears here.
      </p>

      <OutcomeNote outcome={action.outcome} />

      {mayWrite ? (
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <Button
            disabled={action.running}
            onClick={() =>
              void action
                .run(async () => {
                  const res = await adminApi.setApplicationEventSubscriptions(app.id, picked);
                  /* The count is what an admin cannot read back off the ticks
                     they just made, and `deliverable` is the half that decides
                     whether any of it matters. */
                  return subscriptionOutcome(res);
                })
                .then(onSaved)
            }
          >
            {action.running ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Retiring the application, and putting it back.
 *
 * ── It sits in the RECORD HEADER, not in the Settings form ───────────
 *
 * Retiring is not a field. It changes what the application IS — a retired app
 * grants nobody access, and every agreement scoped to it stops being owed by
 * anybody — so it belongs with the record rather than among the things you edit
 * and press Save on. Next to the Save button it read as one more setting, and it
 * was reachable only from one of three tabs.
 *
 * This is the same slot the Security groups screen puts Delete in, which is what
 * makes "the destructive thing lives top right, next to the record" a rule
 * somebody can learn once.
 *
 * ── Still a modal, and deliberately ──────────────────────────────────
 *
 * Record DETAILS became screens; confirmations did not. Retyping the name is the
 * pause, not the authorisation — the server authorises this on the admin's own
 * session. What nothing else catches is retiring the right-LOOKING application
 * out of a list of similar ones, and a dialog you have to type into is what
 * stops that.
 *
 * A HOLD would be the other answer and is wrong here: a hold is for a control
 * that sits in a row somebody is scrolling past. This one is already alone in a
 * header, and holding does not make you check WHICH application you are on.
 */
function RetireControl({ app, onChanged }: { app: Application; onChanged: () => void }) {
  const [retiring, setRetiring] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const action = useAction();

  // One place to leave the confirmation, so the typed name cannot survive a
  // cancel and pre-arm the button the next time the dialog is opened.
  const closeRetire = () => {
    setRetiring(false);
    setConfirmName('');
  };

  if (!app.active) {
    return (
      <span style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
        <OutcomeNote outcome={action.outcome} />
        <Button
          disabled={action.running}
          onClick={() =>
            void action
              .run(async () => {
                await adminApi.updateApplication(app.id, { active: true });
                return 'Restored. Its grants and scoped agreements apply again.';
              })
              .then(onChanged)
          }
        >
          {action.running ? 'Working…' : 'Restore'}
        </Button>
      </span>
    );
  }

  return (
    <span style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
      <OutcomeNote outcome={action.outcome} />
      <Button
        tone="danger"
        onClick={() => {
          setConfirmName('');
          setRetiring(true);
        }}
        disabled={action.running}
      >
        Retire
      </Button>

      {retiring ? (
        <Modal
          title={`Retire ${app.name}`}
          onClose={closeRetire}
          width={460}
          footer={
            <ModalActions>
              <Button onClick={closeRetire}>Cancel</Button>
              <HoldButton
                title="Hold for a second and a half to retire this application."
                disabled={action.running || !confirmsRetire(confirmName, app.name)}
                onHold={() =>
                  void action
                    .run(async () => {
                      const r = await adminApi.updateApplication(app.id, { active: false });
                      return r.affectedAgreements > 0
                        ? `Retired. ${r.affectedAgreements} active agreement(s) scoped to it are no longer owed by anybody.`
                        : 'Retired. No agreements were scoped to it.';
                    })
                    .then(() => {
                      closeRetire();
                      onChanged();
                    })
                }
              >
                {action.running ? 'Working…' : 'Retire it'}
              </HoldButton>
            </ModalActions>
          }
        >
          <div style={{ display: 'grid', gap: '1rem' }}>
            <Note tone="danger">
              A retired application grants nobody access, and agreements scoped to it stop being
              owed by anybody. That is intended, because a dead app&rsquo;s consent requirements should not
              follow people around, but it is a much larger effect than hiding it from a list.
            </Note>
            <Field
              label={`Type ${app.name} to confirm`}
              hint="Retiring can be undone with Restore, but the agreements it releases are not re-owed until then."
            >
              <input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                style={inputStyle}
                autoComplete="off"
                spellCheck={false}
                aria-label={`Type ${app.name} to confirm retiring it`}
              />
            </Field>
          </div>
        </Modal>
      ) : null}
    </span>
  );
}

/**
 * The stored URL, and what the LAST RECORDED probe found.
 *
 * ── Why this reads the log rather than taking a result from the list ─
 *
 * As a modal, this tab was handed the result of whatever check the Applications
 * screen had run in that browsing session, and said "Not checked in this
 * session" otherwise. A screen does not share state with the list — it can be
 * arrived at from a link, or from another tab — so that prop would have made the
 * tab permanently say "not checked", which is a screen that is always wrong.
 *
 * `uptime()` is the honest source: a GET that reports the row the last probe
 * already wrote, which is exactly what System Health reads. It carries the state
 * per application and nothing else — no status code, no latency — which is why
 * `HealthState` takes a state plus whatever detail happens to exist (org-admin#57).
 *
 * STILL NO PROBE ON MOUNT. Opening a record must not make outbound requests to
 * that record's service; the probe stays a POST behind a button on the
 * Applications screen and a timer somebody set on System health.
 *
 * ── A null state is not "healthy" ────────────────────────────────────
 *
 * It means this application was not in the last run: registered since, or never
 * checked at all. The server deliberately does not carry a state forward from an
 * older run, and neither does this — reporting an app as up on the strength of a
 * check that no longer describes it is the failure both are avoiding.
 */
function Health({ app }: { app: Application }) {
  const { state } = useAsync(() => adminApi.uptime(), []);

  if (!app.healthCheckUrl) {
    return (
      <Panel title="Health check">
        <PanelBody>
          <Note>
            No health check URL configured. Add one under Settings, and it is checked whenever
            somebody runs the check on the Applications screen or leaves System health open.
          </Note>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <Panel title="Health check">
      <PanelBody>
        <Field label="Configured URL">
          <input value={app.healthCheckUrl} readOnly style={{ ...inputStyle, background: C.page }} />
        </Field>

        <AsyncBoundary state={state}>
          {(report) => {
            const mine = report.services.find((svc) => svc.id === app.id);
            if (!mine?.state) {
              return (
                <Note>
                  Not in the last recorded check. That is what an application registered since the
                  last run looks like, and what one looks like before anybody has run it — not a
                  result.
                </Note>
              );
            }
            return (
              <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <HealthState state={mine.state} />
                <span style={{ fontSize: '.83rem', color: C.muted }}>
                  last recorded <When iso={report.lastCheckedAt} />
                </span>
              </div>
            );
          }}
        </AsyncBoundary>
      </PanelBody>
    </Panel>
  );
}

/**
 * A result, as a state and a reason.
 *
 * `refused` is its OWN state, distinct from failing. "We would not make this
 * request" and "the service did not answer" are different facts, and collapsing
 * them would send somebody to debug a healthy service because the portal
 * declined to probe a private address.
 *
 * ── It takes the STATE, not a whole probe result (org-admin#57) ──────
 *
 * System Health reads the last recorded run out of the platform log, and that
 * row carries the state per slug and nothing else — no status code, no latency,
 * because the event was never meant to be a metrics store. So the parameter is
 * the state plus whatever detail happens to be available.
 *
 * Widened rather than copied. What this component really owns is which colour
 * each state is, and two screens disagreeing about whether `refused` is amber
 * or red is precisely the drift a second copy would introduce.
 */
export function HealthState({
  state,
  status,
  latencyMs,
  code,
}: {
  state: HealthResult['state'];
  status?: number;
  latencyMs?: number;
  code?: string;
}) {
  if (state === 'ok') {
    /* The status code and latency are a live probe's detail. Absent — reading a
       recorded run — the pill says ok and stops, rather than rendering
       "undefined · —" and looking like a bug in the check. */
    const detail = [status, latencyMs === undefined ? null : duration(latencyMs)]
      .filter((v) => v !== null && v !== undefined)
      .join(' · ');
    return <DotPill tone="good">{detail || 'ok'}</DotPill>;
  }
  if (state === 'failing') return <DotPill tone="danger">{status ?? 'failing'}</DotPill>;
  if (state === 'refused') return <DotPill tone="warn">{code ? `refused: ${code}` : 'refused'}</DotPill>;
  return <DotPill tone="danger">no answer</DotPill>;
}

export function GrantCheckboxes({
  all,
  labels,
  picked,
  onChange,
}: {
  all: string[];
  /** Display text per value. The VALUE is still what is sent — a group's slug is
   *  its stable identity, and its name can be edited. */
  labels?: Record<string, string>;
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  // Anything granted that the server does not list is shown too. A grant that
  // vanishes from the screen while still being in the database is the one thing
  // this control must not do.
  const extra = picked.filter((r) => !all.includes(r));
  return (
    <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap' }}>
      {[...all, ...extra].map((r) => (
        <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: '.86rem' }}>
          <input
            type="checkbox"
            checked={picked.includes(r)}
            onChange={() =>
              onChange(picked.includes(r) ? picked.filter((x) => x !== r) : [...picked, r])
            }
          />
          {labels?.[r] ?? r}
          {extra.includes(r) ? <Badge tone="danger">unknown</Badge> : null}
        </label>
      ))}
    </div>
  );
}

/* The Settings field's way into the platforms modal. */
const editLink = {
  border: `1px dashed ${C.inputBorder}`,
  background: 'transparent',
  color: C.brand,
  borderRadius: 999,
  padding: '.22rem .6rem',
  fontSize: '.82rem',
  fontWeight: 600,
  cursor: 'pointer',
};
